import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Listbox } from "@headlessui/react";
import { CheckIcon, ChevronUpDownIcon } from "@heroicons/react/20/solid";
import { toast } from "react-toastify";
import { fetchPayrollCohorts } from "../../store/thunks/payrollCohortThunk";
import { updateClinic } from "../../store/thunks/clinicThunk";

export default function ClinicPayrollSettings({ clinic, onUpdateClinic, editMode }) {
  const dispatch = useDispatch();
  const payrollCohorts = useSelector((state) => state.payrollCohorts?.payrollCohorts || []);
  const auth = useSelector((state) => state.auth);
  
  const [selectedCohort, setSelectedCohort] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateStatus, setUpdateStatus] = useState("");

  // Fetch payroll cohorts on component mount
  useEffect(() => {
    dispatch(fetchPayrollCohorts());
  }, [dispatch]);

  // Set initial selected cohort when clinic and payrollCohorts are loaded
  useEffect(() => {
    if (clinic?.payrollCohort && payrollCohorts.length > 0) {
      // Clinic has a saved payrollCohort - find and select it
      const cohort = payrollCohorts.find(c => 
        c._id === clinic.payrollCohort._id || c._id === clinic.payrollCohort
      );
      setSelectedCohort(cohort || null);
    } else if (!clinic?.payrollCohort) {
      // Brand new clinic with no payrollCohort - show "Select a cohort"
      setSelectedCohort(null);
    }
  }, [clinic, payrollCohorts]);

  const handleCohortChange = async (newCohort) => {
    if (!editMode) return;
    
    setIsUpdating(true);
    setUpdateStatus("");
    
    try {
      const resultAction = await dispatch(updateClinic({
        id: clinic._id,
        clinicData: { payrollCohort: newCohort?._id || null }
      }));
      
      if (updateClinic.fulfilled.match(resultAction)) {
        const updatedClinic = resultAction.payload;
        setSelectedCohort(newCohort);
        setUpdateStatus("success");
        onUpdateClinic(updatedClinic);
        
        const toastMessage = newCohort 
          ? `Payroll cohort updated to "${newCohort.name}" successfully`
          : "Payroll cohort removed successfully";
        toast.success(toastMessage);
        
        setTimeout(() => setUpdateStatus(""), 3000);
      }
    } catch (error) {
      console.error("Error updating clinic payroll cohort:", error);
      setUpdateStatus("error");
      setTimeout(() => setUpdateStatus(""), 3000);
    } finally {
      setIsUpdating(false);
    }
  };

  const selectedCohortObj = selectedCohort;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Payroll Configuration
        </h3>
        <p className="text-sm text-gray-600 mb-6">
          Configure payroll settings for {clinic.name}. The payroll cohort determines 
          which payroll processing group this clinic belongs to.
        </p>
      </div>

      <div className="bg-gray-50 rounded-sm p-6">
        <div className="max-w-md">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Payroll Cohort
          </label>
          
          <Listbox 
            value={selectedCohortObj} 
            onChange={handleCohortChange}
            disabled={!editMode || isUpdating}
          >
            <div className="relative">
              <Listbox.Button className={`relative w-full cursor-pointer rounded border py-2 pl-3 pr-10 text-left shadow-sm transition-colors ${
                editMode && !isUpdating
                  ? "border-gray-300 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  : "border-gray-200 bg-gray-50 cursor-not-allowed"
              }`}>
                <span className="block truncate text-sm">
                  {selectedCohortObj?.name || (selectedCohortObj === null ? "No Cohort" : "Select a cohort")}
                </span>
                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                  <ChevronUpDownIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </span>
              </Listbox.Button>
              
              {editMode && (
                <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded border border-gray-200 bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                  {/* No Cohort option */}
                  <Listbox.Option
                    key="no-cohort"
                    value={null}
                    className={({ active }) =>
                      `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                        active ? "bg-gray-50 text-gray-900" : "text-gray-500"
                      }`
                    }
                  >
                    {({ selected }) => (
                      <>
                        <span className={`block truncate italic ${selected ? "font-medium" : "font-normal"}`}>
                          No Cohort
                        </span>
                        {selected && (
                          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-gray-600">
                            <CheckIcon className="h-5 w-5" aria-hidden="true" />
                          </span>
                        )}
                      </>
                    )}
                  </Listbox.Option>
                  
                  {/* Separator */}
                  {payrollCohorts.length > 0 && (
                    <hr className="border-gray-200 my-1" />
                  )}
                  
                  {/* Cohort options */}
                  {payrollCohorts.map((cohort) => (
                    <Listbox.Option
                      key={cohort._id}
                      value={cohort}
                      className={({ active }) =>
                        `relative cursor-pointer select-none py-2 pl-10 pr-4 ${
                          active ? "bg-indigo-50 text-indigo-900" : "text-gray-900"
                        }`
                      }
                    >
                      {({ selected }) => (
                        <>
                          <span className={`block truncate ${selected ? "font-medium" : "font-normal"}`}>
                            {cohort.name}
                          </span>
                          {selected && (
                            <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-indigo-600">
                              <CheckIcon className="h-5 w-5" aria-hidden="true" />
                            </span>
                          )}
                        </>
                      )}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              )}
            </div>
          </Listbox>
          
          {/* Status messages */}
          {updateStatus === "success" && (
            <p className="mt-2 text-sm text-green-600">
              Payroll cohort updated successfully
            </p>
          )}
          {updateStatus === "error" && (
            <p className="mt-2 text-sm text-red-600">
              Failed to update payroll cohort. Please try again.
            </p>
          )}
          {isUpdating && (
            <p className="mt-2 text-sm text-gray-500">
              Updating payroll cohort...
            </p>
          )}
          
          <p className="mt-2 text-xs text-gray-500">
            {editMode 
              ? "Select a payroll cohort from the dropdown above" 
              : "Enable edit mode to change the payroll cohort"
            }
          </p>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
        <h4 className="text-sm font-medium text-blue-900 mb-2">
          About Payroll Cohorts
        </h4>
        <div className="text-sm text-blue-800 space-y-2">
          <p>
            <strong>Hearsay:</strong> For Milton clinic
          </p>
          <p>
            <strong>Kekoa Tree:</strong> For all other clinics
          </p>
          <p className="text-xs mt-3 text-blue-700">
            Payroll cohorts help organize and process payrolls across different clinic groups.
          </p>
        </div>
      </div>
    </div>
  );
}